import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useApi, api } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';
import useMobile from '../hooks/useMobile';
import { useTheme } from '../context/ThemeContext';

const COLORS = ['#7C6FF7','#F7605C','#34D399','#FBBF24','#60A5FA','#F472B6','#A78BFA','#FB923C'];

const EMPTY_FORM = {
  first_name:'', last_name:'', email:'', phone:'', phone2:'',
  birthday:'', address:'', company:'', notes:'',
  avatar_color:'#7C6FF7', favorite: false,
};

function getInitials(c) {
  const fn = c.first_name || c.name || '?';
  const ln = c.last_name  || '';
  return `${fn[0]}${ln[0]||''}`.toUpperCase();
}

function getDisplayName(c) {
  if (c.first_name) return `${c.first_name}${c.last_name ? ' ' + c.last_name : ''}`;
  return c.name || '?';
}

export default function ContactsPage() {
  const isMobile = useMobile();
  const { dark } = useTheme();
  const toast = useToast();
  const LIMIT = 24;

  const [searchInput, setSearchInput] = useState('');
  const [search,      setSearch]      = useState('');
  const [view,        setView]        = useState('grid');
  const [page,        setPage]        = useState(1);
  const [showForm,    setShowForm]    = useState(false);
  const [showImport,  setShowImport]  = useState(false);
  const [editId,      setEditId]      = useState(null);
  const [importing,   setImporting]   = useState('');
  const [importMsg,   setImportMsg]   = useState('');
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [syncingBirthdays, setSyncingBirthdays] = useState(false);
  const fileRef = useRef();

  // Debounce søk — send API-kall tidligst 350 ms etter siste tastetrykk
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Scroll til toppen ved sideskift
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [page]);

  const { data, loading, refresh } = useApi(
    `/api/contacts?page=${page}&limit=${LIMIT}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
    [page, search]
  );

  const contacts   = data?.contacts   || [];
  const pagination = data?.pagination || null;
  const totalCount = pagination?.total ?? 0;
  const favCount   = pagination?.favorites ?? 0;

  const set = k => e => setForm(f => ({
    ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value
  }));

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setForm({
      first_name:   c.first_name || c.name?.split(' ')[0] || '',
      last_name:    c.last_name  || c.name?.split(' ').slice(1).join(' ') || '',
      email:        c.email      || '',
      phone:        c.phone      || '',
      phone2:       c.phone2     || '',
      birthday:     c.birthday   || '',
      address:      c.address    || '',
      company:      c.company    || '',
      notes:        c.notes      || '',
      avatar_color: c.avatar_color || c.color || '#7C6FF7',
      favorite:     c.favorite === 1,
    });
    setEditId(c.id);
    setShowForm(true);
  };

  const save = async e => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/api/contacts/${editId}`, form);
      else        await api.post('/api/contacts', form);
      setShowForm(false); setEditId(null); setForm(EMPTY_FORM);
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Feil ved lagring');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Slett denne kontakten?')) return;
    await api.delete(`/api/contacts/${id}`);
    setShowForm(false); setEditId(null);
    refresh();
  };

  const toggleFav = async (id, e) => {
    e?.stopPropagation();
    await api.put(`/api/contacts/${id}/favorite`, {});
    refresh();
  };

  const importGoogle = async () => {
    setImporting('google'); setImportMsg('');
    try {
      const token = localStorage.getItem('token');
      const res   = await axios.post('/api/contacts/import/google', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImportMsg(`✅ ${res.data.message}`);
      refresh();
    } catch (err) {
      setImportMsg('❌ ' + (err.response?.data?.error || 'Import feilet'));
    } finally { setImporting(''); }
  };

  const importFile = async (file) => {
    const type = file.name.endsWith('.vcf') ? 'vcard' : 'csv';
    setImporting(type); setImportMsg('');
    try {
      const text  = await file.text();
      const token = localStorage.getItem('token');
      const res   = await axios.post(
        `/api/contacts/import/${type}`,
        type === 'vcard' ? { vcfContent: text } : { csvContent: text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImportMsg(`✅ ${res.data.message}`);
      refresh();
    } catch (err) {
      setImportMsg(`❌ ${type.toUpperCase()} import feilet`);
    } finally { setImporting(''); }
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.name.endsWith('.vcf') || file.name.endsWith('.csv')) importFile(file);
    else alert('Kun .vcf og .csv filer støttes');
    e.target.value = '';
  };

  const exportVCard = () => {
    const token = localStorage.getItem('token');
    const a = document.createElement('a');
    a.href = `/api/contacts/export/vcard?token=${token}`;
    a.download = 'famora-kontakter.vcf';
    a.click();
  };

  const syncBirthdays = async () => {
    setSyncingBirthdays(true);
    try {
      const r = await api.post('/api/contacts/sync-birthdays', {});
      toast.success(`🎂 ${r.created} bursdagshendelser lagt til i kalenderen`);
    } catch {
      toast.error('Kunne ikke synkronisere bursdager');
    } finally {
      setSyncingBirthdays(false);
    }
  };

  const list = contacts;

  const pageBg = dark ? '#0A0818' : '#EDE8FF';
  const txt    = dark ? '#E8E8F8' : '#1A1A35';
  const sub    = dark ? '#9090B8' : '#888';
  const border = dark ? 'rgba(255,255,255,.07)' : 'rgba(124,111,247,0.12)';
  const glassInp = { padding:'12px 14px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:14, fontFamily:'inherit', outline:'none', boxSizing:'border-box' };

  if (isMobile) return (
    <div style={{ background:pageBg, minHeight:'100%', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>

      {/* Gradient Hero Header */}
      <div style={{
        background: dark
          ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
          : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
        padding: '52px 20px 20px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-60, left:-20, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <div>
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:2 }}>Famora</div>
              <div style={{ fontSize:24, fontWeight:800, color:'#fff' }}>Kontakter</div>
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', marginTop:2 }}>
                {totalCount} kontakter
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => setShowImport(v => !v)} style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(10px)', color:'#fff', border:'1px solid rgba(255,255,255,0.25)', borderRadius:12, padding:'8px 12px', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                Importer
              </button>
              <button onClick={openNew} style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(10px)', color:'#fff', border:'1px solid rgba(255,255,255,0.25)', borderRadius:12, padding:'8px 14px', fontWeight:700, fontSize:14, cursor:'pointer' }}>
                + Ny
              </button>
            </div>
          </div>
          {/* Search bar in hero */}
          <input
            value={searchInput} onChange={e=>setSearchInput(e.target.value)}
            placeholder="Søk kontakter..."
            style={{ width:'100%', padding:'11px 14px', borderRadius:12, border:'1px solid rgba(255,255,255,0.2)', background:'rgba(255,255,255,0.15)', color:'#fff', fontSize:14, fontFamily:'inherit', outline:'none', boxSizing:'border-box' }}
          />
        </div>
      </div>

      {/* Import panel */}
      {showImport && (
        <div className="glass-card" style={{ margin:'12px 16px', padding:14 }}>
          <div style={{ fontSize:13, fontWeight:700, color:txt, marginBottom:10 }}>Importer kontakter</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <button onClick={async()=>{setImporting('google');const r=await api.post('/api/contacts/import/google').catch(e=>({error:e.message}));setImportMsg(r?.error?`Feil: ${r.error}`:`✅ ${r?.count||0} kontakter importert`);setImporting('');}}
              style={{ padding:'10px 14px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', fontWeight:700, cursor:'pointer', fontFamily:'inherit', opacity:importing?0.6:1 }}>
              {importing==='google'?'Importerer...':'Google Kontakter'}
            </button>
            <label style={{ padding:'10px 14px', borderRadius:10, border:'1px solid '+border, background: dark?'rgba(255,255,255,0.05)':'rgba(255,255,255,0.7)', color:txt, fontWeight:700, cursor:'pointer', textAlign:'center', fontSize:14 }}>
              vCard / CSV
              <input type="file" accept=".vcf,.csv" ref={fileRef} style={{ display:'none' }} onChange={async e=>{
                if(!e.target.files[0])return;
                const fd=new FormData(); fd.append('file',e.target.files[0]);
                setImporting('file');
                const r=await api.postForm('/api/contacts/import/file',fd).catch(er=>({error:er.message}));
                setImportMsg(r?.error?`Feil: ${r.error}`:`✅ ${r?.count||0} kontakter importert`);
                setImporting('');
              }}/>
            </label>
          </div>
          {importMsg && <div style={{ marginTop:8, fontSize:13, color: importMsg.startsWith('✅')?'#34D399':'#F7605C', fontWeight:600 }}>{importMsg}</div>}
        </div>
      )}

      {/* Kontaktliste */}
      <div style={{ padding:'12px 16px 80px' }}>
        {loading && contacts.length === 0 && (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ marginBottom:8, padding:'14px 16px', display:'flex', alignItems:'center', gap:14,
              background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              borderRadius:14, animation:'shimmer 1.4s ease-in-out infinite',
              animationDelay: `${i * 0.08}s` }}>
              <div style={{ width:46, height:46, borderRadius:'50%', background: dark?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.08)', flexShrink:0 }}/>
              <div style={{ flex:1 }}>
                <div style={{ height:14, borderRadius:7, background: dark?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.08)', marginBottom:8, width:'60%' }}/>
                <div style={{ height:11, borderRadius:6, background: dark?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.05)', width:'40%' }}/>
              </div>
            </div>
          ))
        )}
        {!loading && list.length === 0 && (
          <div style={{ textAlign:'center', padding:'60px 20px', color:sub }}>
            <div style={{ fontSize:48, marginBottom:12 }}>📞</div>
            <div style={{ fontSize:16, fontWeight:700, color:txt }}>{search?'Ingen treff':'Ingen kontakter'}</div>
          </div>
        )}
        {list.map(c => (
          <div key={c.id} onClick={()=>openEdit(c)} className="glass-card" style={{ marginBottom:8, padding:'14px 16px', display:'flex', alignItems:'center', gap:14, cursor:'pointer' }}>
            <div style={{ width:46, height:46, borderRadius:'50%', background:c.avatar_color||'#7C6FF7', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:17, color:'#fff', flexShrink:0 }}>
              {getInitials(c)}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:15, fontWeight:700, color:txt, marginBottom:2 }}>{getDisplayName(c)}</div>
              {c.phone && <div style={{ fontSize:13, color:sub }}>📱 {c.phone}</div>}
              {c.company && <div style={{ fontSize:12, color:sub }}>{c.company}</div>}
            </div>
            <div style={{ display:'flex', gap:6 }}>
              {c.phone && <a href={`tel:${c.phone}`} onClick={e=>e.stopPropagation()} style={{ background:'rgba(52,211,153,0.15)', color:'#34D399', border:'none', borderRadius:10, width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none', fontSize:16 }}>📞</a>}
              <button onClick={e=>{e.stopPropagation();toggleFav(c.id,e);}} style={{ background:c.favorite?'rgba(251,191,36,0.15)':dark?'rgba(255,255,255,0.08)':'rgba(124,111,247,0.10)', border:'none', borderRadius:10, width:36, height:36, fontSize:16, cursor:'pointer' }}>
                {c.favorite?'⭐':'☆'}
              </button>
            </div>
          </div>
        ))}
        {pagination && (
          <div style={{ display:'flex', gap:10, justifyContent:'center', padding:'10px 0 4px' }}>
            <button disabled={page<=1} onClick={()=>setPage(p=>p-1)} style={{ background:page>1?'linear-gradient(135deg,#7C6FF7,#9358E8)':dark?'rgba(255,255,255,0.08)':'rgba(124,111,247,0.10)', color:page>1?'#fff':sub, border:'none', borderRadius:10, padding:'9px 20px', fontWeight:700, cursor:'pointer' }}>← Forrige</button>
            <span style={{ display:'flex', alignItems:'center', fontSize:13, color:sub }}>{page}/{pagination.totalPages}</span>
            <button disabled={page>=pagination.totalPages} onClick={()=>setPage(p=>p+1)} style={{ background:page<pagination.totalPages?'linear-gradient(135deg,#7C6FF7,#9358E8)':dark?'rgba(255,255,255,0.08)':'rgba(124,111,247,0.10)', color:page<pagination.totalPages?'#fff':sub, border:'none', borderRadius:10, padding:'9px 20px', fontWeight:700, cursor:'pointer' }}>Neste →</button>
          </div>
        )}
      </div>

      {/* Form modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:200 }} onClick={()=>setShowForm(false)}>
          <div style={{ position:'absolute', bottom:0, left:0, right:0, background: dark?'#1A1245':'#fff', borderRadius:'20px 20px 0 0', padding:20, maxHeight:'90vh', overflowY:'auto' }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontSize:16, fontWeight:800, color:txt, marginBottom:14 }}>{editId?'Rediger kontakt':'Ny kontakt'}</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              <input style={{...glassInp}} placeholder="Fornavn" value={form.first_name} onChange={set('first_name')}/>
              <input style={{...glassInp}} placeholder="Etternavn" value={form.last_name} onChange={set('last_name')}/>
            </div>
            <input style={{...glassInp, width:'100%', marginBottom:10}} placeholder="Telefon" value={form.phone} onChange={set('phone')} type="tel"/>
            <input style={{...glassInp, width:'100%', marginBottom:10}} placeholder="E-post" value={form.email} onChange={set('email')} type="email"/>
            <input style={{...glassInp, width:'100%', marginBottom:14}} placeholder="Firma" value={form.company} onChange={set('company')}/>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={save} style={{ flex:1, background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', border:'none', borderRadius:12, padding:13, fontWeight:700, fontSize:15, cursor:'pointer' }}>Lagre</button>
              <button onClick={()=>setShowForm(false)} style={{ flex:1, background:dark?'rgba(255,255,255,0.08)':'rgba(124,111,247,0.10)', color:txt, border:'none', borderRadius:12, padding:13, fontWeight:700, fontSize:15, cursor:'pointer' }}>Avbryt</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const inp = {
    width:'100%', padding:'9px 12px', borderRadius:10,
    border:'1px solid var(--input-bdr)', background:'var(--input-bg)',
    color:'var(--input-txt)', fontFamily:'inherit', fontSize:13,
    outline:'none', boxSizing:'border-box',
  };
  const lbl = {
    fontSize:11, fontWeight:700, color:'var(--txl)',
    textTransform:'uppercase', letterSpacing:'.5px',
    display:'block', marginBottom:4,
  };

  return (
    <div style={{ maxWidth:1000 }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:800, color:'var(--txt)', margin:0 }}>Kontakter</h2>
          <p style={{ fontSize:13, color:'var(--txl)', margin:'4px 0 0' }}>
            {totalCount} kontakter{favCount > 0 ? ` · ${favCount} favoritter` : ''}
          </p>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button style={s.sec} onClick={() => setShowImport(v => !v)}>⬇️ Importer</button>
          <button style={s.sec} onClick={exportVCard}>⬆️ Eksporter</button>
          <button style={s.sec} onClick={syncBirthdays} disabled={syncingBirthdays}
            title="Legg til bursdager fra kontakter som hendelser i kalenderen">
            {syncingBirthdays ? '⏳ Synkroniserer…' : '🎂 Bursdager → Kalender'}
          </button>
          <button style={s.add} onClick={openNew}>+ Ny kontakt</button>
        </div>
      </div>

      {/* Import-panel */}
      {showImport && (
        <div style={s.card}>
          <div style={{ fontSize:15, fontWeight:700, color:'var(--txt)', marginBottom:14 }}>Importer kontakter</div>

          {importMsg && (
            <div style={{
              padding:'10px 14px', borderRadius:10, marginBottom:14, fontSize:13,
              background: importMsg.startsWith('✅') ? 'var(--green-bg)' : 'var(--red-bg)',
              color: importMsg.startsWith('✅') ? 'var(--green)' : 'var(--red)',
            }}>{importMsg}</div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10 }}>
            {[
              { key:'google', label:'Google Kontakter', icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" style={{margin:'0 auto 6px',display:'block'}}>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              ), sub:'Synkroniser fra Google', onClick: importGoogle, disabled: importing === 'google' },
              { key:'vcard', label: importing==='vcard' ? 'Importerer…' : 'vCard (.vcf)', icon:<div style={{fontSize:28,textAlign:'center',marginBottom:6}}>📇</div>, sub:'iPhone, Android, Outlook', onClick:()=>fileRef.current?.click(), disabled:!!importing },
              { key:'csv',   label: importing==='csv'   ? 'Importerer…' : 'CSV-fil', icon:<div style={{fontSize:28,textAlign:'center',marginBottom:6}}>📊</div>, sub:'Excel, regneark', onClick:()=>fileRef.current?.click(), disabled:!!importing },
              { key:'icloud',label:'iCloud', icon:<div style={{fontSize:28,textAlign:'center',marginBottom:6}}>☁️</div>, sub:'Eksporter .vcf fra iCloud.com', onClick:null, disabled:true, info:true },
            ].map(item => (
              <button key={item.key} onClick={item.onClick || undefined} disabled={item.disabled}
                style={{ ...s.importCard, opacity:item.info?0.6:1, cursor:item.info?'default':item.disabled?'wait':'pointer' }}>
                {item.icon}
                <div style={{ fontWeight:700, fontSize:13, color:'var(--txt)' }}>
                  {importing === item.key ? 'Importerer…' : item.label}
                </div>
                <div style={{ fontSize:11, color:'var(--txl)', marginTop:3 }}>{item.sub}</div>
              </button>
            ))}
          </div>

          <input ref={fileRef} type="file" accept=".vcf,.csv" style={{display:'none'}} onChange={handleFileInput} />

          <div style={{ marginTop:12, fontSize:12, color:'var(--txl)', lineHeight:1.6 }}>
            💡 <strong>iPhone:</strong> Innstillinger → iCloud → Kontakter → Eksporter vCard.
            &nbsp;<strong>Android:</strong> Kontakter-appen → Importer/Eksporter → Eksporter til .vcf
          </div>
        </div>
      )}

      {/* Skjema */}
      {showForm && (
        <div style={s.card}>
          <div style={{ fontSize:15, fontWeight:700, color:'var(--txt)', marginBottom:16 }}>
            {editId ? 'Rediger kontakt' : 'Ny kontakt'}
          </div>
          <form onSubmit={save}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <div><label style={lbl}>Fornavn *</label><input style={inp} value={form.first_name} onChange={set('first_name')} placeholder="Fornavn" required autoFocus /></div>
              <div><label style={lbl}>Etternavn</label><input style={inp} value={form.last_name} onChange={set('last_name')} placeholder="Etternavn" /></div>
              <div><label style={lbl}>E-post</label><input style={inp} type="email" value={form.email} onChange={set('email')} placeholder="epost@example.com" /></div>
              <div><label style={lbl}>Telefon</label><input style={inp} value={form.phone} onChange={set('phone')} placeholder="+47 123 45 678" /></div>
              <div><label style={lbl}>Telefon 2</label><input style={inp} value={form.phone2} onChange={set('phone2')} placeholder="Alternativt nummer" /></div>
              <div><label style={lbl}>Fødselsdato</label><input style={inp} type="date" value={form.birthday} onChange={set('birthday')} /></div>
              <div><label style={lbl}>Firma</label><input style={inp} value={form.company} onChange={set('company')} placeholder="Firmanavn" /></div>
              <div><label style={lbl}>Adresse</label><input style={inp} value={form.address} onChange={set('address')} placeholder="Gateadresse, by" /></div>
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={lbl}>Notat</label>
              <textarea style={{ ...inp, height:68, resize:'vertical' }} value={form.notes} onChange={set('notes')} placeholder="Notater…" />
            </div>
            <div style={{ display:'flex', gap:16, alignItems:'center', marginBottom:14, flexWrap:'wrap' }}>
              <div>
                <label style={{ ...lbl, marginBottom:6 }}>Farge</label>
                <div style={{ display:'flex', gap:6 }}>
                  {COLORS.map(c => (
                    <div key={c} onClick={() => setForm(f => ({...f, avatar_color:c}))}
                      style={{ width:24, height:24, borderRadius:'50%', background:c, cursor:'pointer',
                        border: form.avatar_color===c ? '3px solid var(--txt)' : '3px solid transparent' }} />
                  ))}
                </div>
              </div>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:14, color:'var(--txt)', marginTop:14 }}>
                <input type="checkbox" checked={form.favorite} onChange={set('favorite')} />
                Favoritt
              </label>
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button type="submit" style={s.add}>{editId ? 'Lagre endringer' : 'Legg til'}</button>
              <button type="button" style={s.sec} onClick={() => { setShowForm(false); setEditId(null); }}>Avbryt</button>
              {editId && (
                <button type="button" style={{ ...s.sec, color:'var(--red)', marginLeft:'auto' }}
                  onClick={() => del(editId)}>Slett</button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Søk + visningsvalg */}
      <div style={{ display:'flex', gap:8, marginBottom:14, alignItems:'center' }}>
        <input style={{ ...inp, flex:1, margin:0 }} placeholder="Søk i kontakter…"
          value={searchInput} onChange={e => setSearchInput(e.target.value)} />
        {['grid','list'].map(v => (
          <button key={v} style={{ ...s.sec, background: view===v?'var(--accent)':'var(--card)', color: view===v?'white':'var(--txm)' }}
            onClick={() => setView(v)}>
            {v === 'grid' ? '⊞' : '☰'}
          </button>
        ))}
      </div>

      {/* Tom-tilstand */}
      {list.length === 0 && (
        <div style={{ textAlign:'center', padding:'60px 20px', ...s.card }}>
          <div style={{ fontSize:48, marginBottom:12 }}>👥</div>
          <div style={{ fontSize:16, fontWeight:600, color:'var(--txt)', marginBottom:6 }}>
            {search ? 'Ingen kontakter funnet' : 'Ingen kontakter ennå'}
          </div>
          <div style={{ fontSize:13, color:'var(--txl)' }}>
            {search ? 'Prøv et annet søkeord' : 'Legg til kontakter manuelt eller importer fra Google'}
          </div>
        </div>
      )}

      {/* Grid-visning */}
      {view === 'grid' && list.length > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))', gap:10 }}>
          {list.map(c => (
            <div key={c.id} onClick={() => openEdit(c)}
              style={{ ...s.card, textAlign:'center', cursor:'pointer', position:'relative', padding:'20px 12px' }}>
              {c.favorite === 1 && (
                <button onClick={e => toggleFav(c.id,e)} style={{ position:'absolute',top:8,right:8,background:'none',border:'none',cursor:'pointer',fontSize:14 }}>⭐</button>
              )}
              <div style={{ width:52, height:52, borderRadius:'50%', background:c.avatar_color||c.color||'#7C6FF7', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:19, color:'white', margin:'0 auto 10px' }}>
                {getInitials(c)}
              </div>
              <div style={{ fontWeight:700, fontSize:13, color:'var(--txt)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:3 }}>
                {getDisplayName(c)}
              </div>
              {c.company && <div style={{ fontSize:11, color:'var(--txl)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:3 }}>{c.company}</div>}
              {c.phone   && <div style={{ fontSize:12, color:'var(--txm)' }}>{c.phone}</div>}
              {c.source && c.source !== 'manual' && (
                <div style={{ position:'absolute',bottom:6,right:8,fontSize:10,color:'var(--txl)' }}>
                  {c.source==='google'?'G':c.source==='vcard'?'📇':c.source==='csv'?'📊':''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Liste-visning */}
      {view === 'list' && list.map(c => (
        <div key={c.id} onClick={() => openEdit(c)}
          style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', ...s.card, cursor:'pointer', marginBottom:6, flexDirection:undefined }}>
          <div style={{ width:40, height:40, borderRadius:'50%', background:c.avatar_color||c.color||'#7C6FF7', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:15, color:'white', flexShrink:0 }}>
            {getInitials(c)}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:700, fontSize:14, color:'var(--txt)' }}>
              {getDisplayName(c)}{c.favorite===1?' ⭐':''}
            </div>
            <div style={{ fontSize:12, color:'var(--txl)', display:'flex', gap:10, flexWrap:'wrap', marginTop:2 }}>
              {c.email   && <span>{c.email}</span>}
              {c.phone   && <span>{c.phone}</span>}
              {c.company && <span>{c.company}</span>}
            </div>
          </div>
          <div style={{ fontSize:11, color:'var(--txl)', flexShrink:0 }}>
            {c.source==='google'?'Google':c.source==='vcard'?'vCard':c.source==='csv'?'CSV':''}
          </div>
        </div>
      ))}

      {/* Paginering */}
      {pagination && pagination.totalPages > 1 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginTop:24, flexWrap:'wrap' }}>
          <button onClick={() => setPage(p => p - 1)} disabled={!pagination.hasPrev}
            style={{ ...s.pageBtn, opacity: pagination.hasPrev ? 1 : 0.35, cursor: pagination.hasPrev ? 'pointer' : 'default' }}>
            ← Forrige
          </button>

          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
            .filter(n => n === 1 || n === pagination.totalPages || Math.abs(n - pagination.page) <= 2)
            .reduce((acc, n, i, arr) => {
              if (i > 0 && n - arr[i - 1] > 1) acc.push('…');
              acc.push(n);
              return acc;
            }, [])
            .map((item, i) =>
              item === '…' ? (
                <span key={`d${i}`} style={{ padding:'0 4px', color:'var(--txl)', fontSize:14 }}>…</span>
              ) : (
                <button key={item} onClick={() => setPage(item)}
                  style={{
                    ...s.pageBtn,
                    background:   pagination.page === item ? 'var(--accent)' : 'var(--card)',
                    color:        pagination.page === item ? 'white'         : 'var(--txm)',
                    borderColor:  pagination.page === item ? 'var(--accent)' : 'var(--bdr)',
                    fontWeight:   pagination.page === item ? 700 : 500,
                  }}>
                  {item}
                </button>
              )
            )
          }

          <button onClick={() => setPage(p => p + 1)} disabled={!pagination.hasNext}
            style={{ ...s.pageBtn, opacity: pagination.hasNext ? 1 : 0.35, cursor: pagination.hasNext ? 'pointer' : 'default' }}>
            Neste →
          </button>
        </div>
      )}

      {/* Info under paginering */}
      {pagination && pagination.total > 0 && (
        <div style={{ textAlign:'center', marginTop:10, fontSize:12, color:'var(--txl)' }}>
          Viser {(pagination.page - 1) * LIMIT + 1}–{Math.min(pagination.page * LIMIT, pagination.total)} av {pagination.total} kontakter
        </div>
      )}
    </div>
  );
}

const s = {
  add:        { padding:'9px 18px', borderRadius:10, border:'none', background:'var(--accent)', color:'white', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  sec:        { padding:'9px 14px', borderRadius:10, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  card:       { background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:14, padding:20, marginBottom:14 },
  importCard: { background:'var(--bg2)', border:'1px solid var(--bdr)', borderRadius:12, padding:'16px 12px', textAlign:'center', fontFamily:'inherit' },
  pageBtn:    { padding:'7px 12px', borderRadius:8, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', fontWeight:500, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
};
